// src/components/quizzes/BonusGreaterThan.js
//
// Greater than / less than / equal to bonus round.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

/** ======= GAME KNOBS ======= */
const VALUE_MIN = 0;
const VALUE_MAX = 50;
const SPAWN_MS = 450;
const MIN_TARGETS_ONSCREEN = 2;    // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9; // correct numbers fall slightly slower

/** ======= UTIL ======= */
const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const makeRandomProblem = () => {
  const op = pick([">", "<", ">", "<", "=", ">", "<"]);
  const anchor = randomInt(5, 40);
  const blankSide = Math.random() < 0.5 ? "left" : "right";
  return { op, anchor, blankSide };
};

const speakableProblem = ({ op, anchor, blankSide }) => {
  if (blankSide === "right") {
    if (op === ">") return `${anchor} is greater than blank`;
    if (op === "<") return `${anchor} is less than blank`;
    return `${anchor} equals blank`;
  } else {
    if (op === ">") return `blank is greater than ${anchor}`;
    if (op === "<") return `blank is less than ${anchor}`;
    return `blank equals ${anchor}`;
  }
};

const pickInRange = (lo, hi) => {
  if (lo > hi) return null;
  return lo + Math.floor(Math.random() * (hi - lo + 1));
};

// Correctness predicate
const isCorrectFor = ({ op, anchor, blankSide }, n) => {
  if (op === "=") return n === anchor;
  if (op === ">") return blankSide === "right" ? n < anchor : n > anchor;
  return blankSide === "right" ? n > anchor : n < anchor;
};

// Sample a CORRECT value
const pickCorrectValue = (problem) => {
  const { op, anchor, blankSide } = problem;
  if (op === "=") return anchor;

  if (op === ">") {
    return blankSide === "right"
      ? pickInRange(VALUE_MIN, anchor - 1)
      : pickInRange(anchor + 1, VALUE_MAX);
  }

  // op === "<"
  return blankSide === "right"
    ? pickInRange(anchor + 1, VALUE_MAX)
    : pickInRange(VALUE_MIN, anchor - 1);
};

// Sample an INCORRECT value (balanced and varied)
const pickIncorrectValue = (problem) => {
  const { op, anchor, blankSide } = problem;

  if (op === "=") {
    // anything except anchor, uniform over full range
    let v = randomInt(VALUE_MIN, VALUE_MAX);
    if (v === anchor) {
      v = Math.max(VALUE_MIN, Math.min(VALUE_MAX, anchor + (Math.random() < 0.5 ? -1 : 1)));
      if (v === anchor) v = anchor > VALUE_MIN ? VALUE_MIN : VALUE_MAX;
    }
    return v;
  }

  if (op === ">") {
    if (blankSide === "right") {
      // anchor > blank; incorrect if blank >= anchor
      const equal = anchor;
      const above = pickInRange(anchor + 1, VALUE_MAX);
      return Math.random() < 0.25 ? equal : (above ?? equal);
    }
    // blank > anchor; incorrect if blank <= anchor
    const below = pickInRange(VALUE_MIN, anchor - 1);
    const equal = anchor;
    return Math.random() < 0.25 ? equal : (below ?? equal);
  }

  // op === "<"
  if (blankSide === "right") {
    // anchor < blank; incorrect if blank <= anchor
    const below = pickInRange(VALUE_MIN, anchor - 1);
    const equal = anchor;
    return Math.random() < 0.25 ? equal : (below ?? equal);
  } else {
    // blank < anchor; incorrect if blank >= anchor
    const equal = anchor;
    const above = pickInRange(anchor + 1, VALUE_MAX);
    return Math.random() < 0.25 ? equal : (above ?? equal);
  }
};

// Keep ~50/50 mix on screen and ensure some correct are present
const shouldSpawnCorrectBalanced = (problem, items) => {
  const correct = items.filter(it => isCorrectFor(problem, it.value)).length;
  const wrong = items.length - correct;

  if (correct < MIN_TARGETS_ONSCREEN) return true; // ensure availability
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5; // tie-breaker
};

const formatProblem = ({ op, anchor, blankSide }) =>
  blankSide === "right" ? `${anchor} ${op} ____` : `____ ${op} ${anchor}`;

const ruleText = ({ op, anchor, blankSide }) => {
  if (op === "=") return `numbers equal to ${anchor}`;
  if (op === ">") return blankSide === "right" ? `numbers less than ${anchor}` : `numbers greater than ${anchor}`;
  return blankSide === "right" ? `numbers greater than ${anchor}` : `numbers less than ${anchor}`;
};

const GreaterThanBonus = (props) => (
  <BonusShell
    {...props}
    title="Greater, Less or Equal!"
    emoji="⚖️"
    sub="Catch the numbers that make the statement TRUE."
    intro="Greater than, less than, or equal to. Catch the numbers that make the statement true. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "Makes it true", pts: "+5" },
      { icon: "❌", text: "Makes it false", pts: "-1" },
    ]}
    init={() => makeRandomProblem()}
    prompt={(p) => ({ big: formatProblem(p).replace("____", "▢"), small: `Catch ${ruleText(p)}!`, say: `${speakableProblem(p)}. Catch ${ruleText(p)}.` })}
    spawn={(p, onScreen) => {
      const want = shouldSpawnCorrectBalanced(p, onScreen);
      let v = want ? pickCorrectValue(p) : pickIncorrectValue(p);
      if (v == null) v = want ? pickCorrectValue(p) : pickIncorrectValue(p);
      if (v == null) v = randomInt(VALUE_MIN, VALUE_MAX);
      return v;
    }}
    check={isCorrectFor}
    newEvery={4}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default GreaterThanBonus;
